// Simple pages store using localStorage + optional server fallback
const KEY = 'nce_pages_v1';
const API_BASE = process.env.REACT_APP_PAGES_API || 'http://localhost:4001/api';

/**
 * Stored structure: [{ path: '/ninjago', title: 'Ninjago', createdAt: 123456789, content:'', disabled:false }, ...]
 */

async function read() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

async function write(data) {
  try {
    localStorage.setItem(KEY, JSON.stringify(data || []));
    try { window.dispatchEvent(new CustomEvent('pages:changed', { detail: data || [] })); } catch(e) {}
    return data || [];
  } catch (e) {
    return data || [];
  }
}

async function tryFetch(url, opts) {
  try {
    const r = await fetch(url, { cache: 'no-store', ...opts });
    if (!r.ok) {
      const text = await r.text().catch(()=>null);
      console.error('pagesStore: bad response', r.status, text, url, opts);
      throw new Error('bad response');
    }
    return r.json();
  } catch (e) {
    console.error('pagesStore: fetch failed', e, url, opts);
    return null;
  }
}

function mergeByPath(remoteList, localList) {
  const map = new Map();
  const all = [...(Array.isArray(localList) ? localList : []), ...(Array.isArray(remoteList) ? remoteList : [])];
  for (const it of all) {
    if (!it) continue;
    const key = it.path || it.slug || it.id;
    if (!key) continue;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, it);
    } else {
      // Prefer whichever has content; otherwise last one wins
      const prevHasContent = !!(prev.content && String(prev.content).length);
      const curHasContent = !!(it.content && String(it.content).length);
      if (curHasContent && !prevHasContent) {
        map.set(key, { ...prev, ...it });
      } else if (!prevHasContent && !curHasContent) {
        map.set(key, { ...prev, ...it });
      } else if (prevHasContent && curHasContent) {
        // keep existing but merge metadata
        map.set(key, { ...prev, ...it });
      }
    }
  }
  return Array.from(map.values());
}

export async function getPages() {
  const [remote, local] = await Promise.all([
    tryFetch(`${API_BASE}/pages?_=${Date.now()}`),
    read()
  ]);
  if (Array.isArray(remote)) {
    if (Array.isArray(local) && local.length) {
      return mergeByPath(remote, local);
    }
    return remote;
  }
  // fallback to localStorage
  return Array.isArray(local) ? local : [];
}

export async function addPage(page) {
  // try remote create
  try {
    const body = { title: page.title, path: page.path, content: page.content || '' };
    // Use upsert so repeated calls update content (especially popupId/url) reliably across browsers
    const remote = await tryFetch(`${API_BASE}/pages/upsert?_=${Date.now()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    // Regardless of remote success shape, also persist locally with content so clients can resolve URL immediately
    try {
      const pages = await read();
      let path = (page.path || '').trim();
      if (!path) return remote || pages;
      if (!path.startsWith('/')) path = '/' + path;
      const existingIdx = pages.findIndex(p => p.path === path);
      if (existingIdx !== -1) {
        pages[existingIdx] = {
          ...pages[existingIdx],
          title: (page.title || pages[existingIdx].title || path.replace(/^\//, '')),
          content: page.content || pages[existingIdx].content || '',
          disabled: false,
          updatedAt: Date.now(),
        };
      } else {
        pages.unshift({
          id: Date.now().toString(),
          path,
          title: (page.title || '').trim() || path.replace(/^\//, ''),
          createdAt: Date.now(),
          content: page.content || '',
          disabled: !!page.disabled
        });
      }
      await write(pages);
    } catch (e) {
      // ignore local write failures
    }
    if (remote) return remote;
  } catch (e) {
    // continue to local fallback
  }

  // local fallback: modify local list and persist
  const pages = await read();
  let path = (page.path || '').trim();
  if (!path) return pages;
  if (!path.startsWith('/')) path = '/' + path;
  const existingIdx = pages.findIndex(p => p.path === path);
  if (existingIdx !== -1) {
    pages[existingIdx].title = (page.title || pages[existingIdx].title || path.replace(/^\//, ''));
    pages[existingIdx].content = page.content || pages[existingIdx].content || '';
    pages[existingIdx].disabled = false;
    pages[existingIdx].updatedAt = Date.now();
  } else {
    const it = {
      id: Date.now().toString(),
      path,
      title: (page.title || '').trim() || path.replace(/^\//, ''),
      createdAt: Date.now(),
      content: page.content || '',
      disabled: !!page.disabled
    };
    pages.unshift(it);
  }
  await write(pages);
  return pages;
}

export async function removePage(idOrPath) {
  // try remote delete by id or encoded path
  try {
    const remote = await tryFetch(`${API_BASE}/pages/${encodeURIComponent(idOrPath)}?_=${Date.now()}`, { method: 'DELETE' });
    if (remote) return remote;
  } catch (e) {
    // fallback to local
  }

  // fallback localStorage remove
  const list = await read();
  const filtered = list.filter(p => p.id !== idOrPath && p.path !== idOrPath && p.slug !== idOrPath);
  await write(filtered);
  return { success: true };
}

export async function disablePage(path) {
  if (!path) return await read();
  const pages = await read();
  const normalized = path.trim().startsWith('/') ? path.trim() : '/' + path.trim();
  const idx = pages.findIndex(p => p.path === normalized);
  if (idx !== -1) {
    pages[idx].disabled = true;
    pages[idx].updatedAt = Date.now();
  } else {
    pages.unshift({
      id: Date.now().toString(),
      path: normalized,
      title: normalized.replace(/^\//, ''),
      createdAt: Date.now(),
      content: '',
      disabled: true
    });
  }
  await write(pages);
  return pages;
}

export async function clearPages() {
  await write([]);
  return [];
}